import {nullify, ResponseData, showDialog, showSnackbar} from '@dvrd/dvr-controls';
import React, {ForwardedRef, forwardRef, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import BolProduct from '../../../models/bolProductModel';
import {ActivateHandle} from '../../../util/interfaces';
import {getOpenProduct} from '../../../util/requestSender';
import {createErrorMessage} from '../../../util/utils';
import EditNameDialog from './editNameDialog';

interface Props {
    onSuccess: (product: BolProduct) => void;
}

function EditNameDialogController(props: Props, ref: ForwardedRef<ActivateHandle<BolProduct>>) {
    const {onSuccess} = props;
    const {t} = useTranslation();
    const [active, setActive] = useState(false);
    const [product, setProduct] = useState<BolProduct | null>(null);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const resetRun = useRef(0);
    const canSave = useMemo(() => {
        const trimmed = name.trim();
        return !!product && !!trimmed.length && trimmed !== product.title;
    }, [name, product]);
    const bolName = useMemo(() => nullify(product?.bolTitle ?? '') ?? product?.title ?? '', [product]);

    function onActivate(activated?: BolProduct) {
        if (!activated) return;
        // Bumping the run discards an in-flight bol-name lookup from a
        // previously opened product, which would otherwise land in this form.
        ++resetRun.current;
        setProduct(activated.clone());
        setName(activated.title);
        setSaving(false);
        setResetting(false);
        setActive(true);
    }

    function onClose() {
        if (saving) return;
        setActive(false);
    }

    function onChangeName(value: string) {
        setName(value);
    }

    function onResetName() {
        if (!product || saving || resetting) return;
        const run = ++resetRun.current;
        setResetting(true);
        getOpenProduct({
            account_id: product.accountId,
            ean: product.ean,
            callback: (data: ResponseData) => {
                if (run !== resetRun.current) return;
                setResetting(false);
                const title = nullify(data.open_product?.title ?? '');
                if (title) {
                    setName(title);
                    return;
                }
                const fallback = nullify(bolName);
                if (fallback && fallback !== product.title) {
                    setName(fallback);
                    return;
                }
                if (!data.handled)
                    showSnackbar(t('products.bolNameFailed', 'De naam van bol kon niet worden opgehaald.'));
            },
            errorCallback: () => {
                if (run !== resetRun.current) return;
                setResetting(false);
                showSnackbar(t('products.bolNameFailed', 'De naam van bol kon niet worden opgehaald.'));
            }
        });
    }

    function onSubmit() {
        if (!product || !canSave || saving) return;
        const renamed = product.clone();
        renamed.title = name.trim();
        setSaving(true);
        renamed.update(
            false,
            (updated: BolProduct, success: boolean, data: ResponseData) => {
                setSaving(false);
                if (success) {
                    showSnackbar(t('products.editName.saved', 'De productnaam is opgeslagen'));
                    setProduct(updated);
                    onSuccess(updated);
                    setActive(false);
                } else if (!data.handled) showSaveError(data.message);
            },
            () => {
                setSaving(false);
                showSaveError();
            }
        );
    }

    function showSaveError(message?: string | null) {
        showDialog(
            createErrorMessage(
                message ?? t('products.editName.failed', 'Het opslaan van de productnaam is niet gelukt.')
            ),
            t('products.editName.failedTitle', 'Opslaan mislukt')
        );
    }

    useImperativeHandle(ref, () => ({activate: onActivate, close: onClose}));

    return (
        <EditNameDialog
            onClose={onClose}
            onChangeName={onChangeName}
            onResetName={onResetName}
            resetting={resetting}
            onSubmit={onSubmit}
            active={active}
            product={product}
            name={name}
            canSave={canSave}
            saving={saving}
        />
    );
}

export default forwardRef<ActivateHandle<BolProduct>, Props>(EditNameDialogController);
